import { useState, useEffect } from "react";
import { Stack, Group, Text, Divider, Center, Box, rem } from "@mantine/core";
import useIsDesktop from "../../../../hooks/useIsDesktop";
import { Dropzone, MIME_TYPES } from "@mantine/dropzone";
import { HiCloudArrowUp } from "react-icons/hi2";
import { toastService } from "../../../../providers";
import { CustomText } from "../../../ui/CustomText/CustomText";
import CustomButton from "../../../ui/CustomButton/CustomButton";
import CustomModal from "../../../ui/CustomModal/CustomModal";

export const LogoUploadModal = ({
  opened,
  onClose,
  mode = "create",
  onSubmit,
  isSubmitting,
  initialLogo,
}) => {
  const isDesktop = useIsDesktop();
  const isEdit = mode === "edit";
  const title = isEdit ? "Update Company Logo" : "Upload Company Logo";
  const buttonLabel = isEdit ? "Save Changes" : "Upload Logo";

  const [file, setFile] = useState(null);
  const [preview, setPreview] = useState(null);
  const [localSubmitting, setLocalSubmitting] = useState(false);
  const MAX_SIZE_MB = 2;

  useEffect(() => {
    if (opened) {
      setPreview(initialLogo || null);
      setFile(null);
    } else {
      setPreview(null);
      setFile(null);
    }
  }, [opened, initialLogo]);

  useEffect(() => {
    return () => {
      if (preview && preview.startsWith && preview.startsWith("blob:")) {
        try {
          URL.revokeObjectURL(preview);
        } catch {
          //ignore
        }
      }
    };
  }, [preview]);

  const handleDrop = (files) => {
    const f = files[0];

    if (f.size / (1024 * 1024) > MAX_SIZE_MB) {
      toastService.error(`File is too large. Max ${MAX_SIZE_MB}MB allowed.`);
      return;
    }

    if (preview && preview.startsWith && preview.startsWith("blob:")) {
      try {
        URL.revokeObjectURL(preview);
      } catch {
        // ignore
      }
    }

    setFile(f);
    setPreview(URL.createObjectURL(f));
  };

  const handleReject = (rejected) => {
    if (!rejected || !rejected.length) return;
    const item = rejected[0];
    const code = item?.errors?.[0]?.code;
    if (code === "file-too-large") {
      toastService.error(`File is too large. Max ${MAX_SIZE_MB}MB allowed.`);
    } else if (code === "file-invalid-type") {
      toastService.error("Invalid file type. Only PNG/JPEG allowed.");
    } else {
      toastService.error("File not accepted.");
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    if (!file) {
      toastService.info("No changes made to the logo.");
      onClose();
      return;
    }
    if (!file || !(file instanceof Blob)) {
      toastService.error("Please select a valid image file.");
      return;
    }

    if (localSubmitting || isSubmitting) return;
    setLocalSubmitting(true);
    try {
      const base64DataUri = await new Promise((resolve, reject) => {
        const reader = new FileReader();
        reader.onerror = () => reject(new Error("Failed to read file"));
        reader.onload = () => resolve(reader.result);
        reader.readAsDataURL(file);
      });
      await onSubmit({ logoBase64: base64DataUri, contentType: file.type });
      setFile(null);
      setPreview(null);
    } catch (err) {
      toastService.error(err?.message || "Failed to upload logo");
    } finally {
      setLocalSubmitting(false);
    }
  };

  return (
    <CustomModal
      opened={opened}
      onClose={onClose}
      centered
      size="lg"
      radius="md"
      title={
        <Text
          fw={600}
          size="lg"
          style={{ textAlign: "center", color: "#1c7ed6" }}
        >
          {title}
        </Text>
      }
      overlayProps={{ backgroundOpacity: 0.5, blur: 2 }}
      closeOnClickOutside={!isSubmitting && !localSubmitting}
      closeOnEscape={!isSubmitting && !localSubmitting}
      withCloseButton={!isSubmitting && !localSubmitting}
      styles={{ body: { padding: 0 } }}
    >
      <div style={{ maxHeight: "calc(90dvh - 160px)", overflowY: "auto", padding: "0 1rem 1rem" }}>
      <Divider mb="md" />

      <form onSubmit={handleSubmit}>
        <Stack gap="md">
          <Dropzone
            multiple={false}
            onDrop={handleDrop}
            onReject={handleReject}
            accept={[MIME_TYPES.png, MIME_TYPES.jpeg]}
            maxSize={MAX_SIZE_MB * 1024 ** 2}
            aria-label="Company logo dropzone"
            disabled={isSubmitting || localSubmitting}
          >
            <Center mih={180} style={{ flexDirection: "column", gap: rem(8) }}>
              <HiCloudArrowUp style={{ fontSize: 48, color: "#228be6" }} />

              <CustomText fw={600}>
                Choose a file or drag & drop here
              </CustomText>

              <CustomText size="sm" c="dimmed">
                JPEG, PNG, JPG — up to {MAX_SIZE_MB} MB (will be resized to ≤ 2
                MB if needed)
              </CustomText>

              <CustomButton
                variant="light"
                radius="xl"
                mt="xs"
                disabled={isSubmitting || localSubmitting}
              >
                Browse File
              </CustomButton>
            </Center>
          </Dropzone>

          {preview && (
            <Box
              mt="sm"
              style={{
                border: "1px solid #e9ecef",
                padding: "8px",
                borderRadius: "8px",
                textAlign: "center",
              }}
            >
              <CustomText size="sm" mb={6} fw={500}>
                Preview:
              </CustomText>
              <img
                src={preview}
                alt={file ? `Preview of ${file.name}` : "Preview"}
                className="w-32 h-32 object-contain rounded-md mx-auto"
              />

              {file && (
                <CustomText size="xs" c="dimmed" mt={6}>
                  {file.name} — {(file.size / 1024).toFixed(1)} KB
                </CustomText>
              )}
            </Box>
          )}

          <Group mt="md" justify={isDesktop ? "flex-end" : "stretch"} grow={!isDesktop}>
            <CustomButton
              variant="default"
              onClick={onClose}
              disabled={isSubmitting || localSubmitting}
            >
              Cancel
            </CustomButton>

            <CustomButton
              type="submit"
              loading={isSubmitting || localSubmitting}
              disabled={!file || isSubmitting || localSubmitting}
            >
              {buttonLabel}
            </CustomButton>
          </Group>
        </Stack>
      </form>
      </div>
    </CustomModal>
  );
};
